import { useState, useEffect, useCallback } from 'react';
import { useSocket } from './useSocket';

export const useCampaignTracking = () => {
    const { socket, connected, error, emit, useSocketEvent } = useSocket();
    const [campaignData, setCampaignData] = useState({
        statistics: null,
        history: [],
        activeCampaigns: []
    });
    const [loading, setLoading] = useState(true);
    const [realtimeUpdates, setRealtimeUpdates] = useState([]);

    // Campaign statistics update handler
    useSocketEvent('campaign_statistics_updated', (data) => {
        console.log('Campaign statistics updated:', data);
        setCampaignData(prev => ({
            ...prev,
            statistics: data.data
        }));
        
        // Add to realtime updates log
        setRealtimeUpdates(prev => [...prev, {
            type: 'statistics',
            data: data.data,
            timestamp: new Date()
        }]);
    });

    // Campaign history update handler
    useSocketEvent('campaign_history_updated', (data) => {
        console.log('Campaign history updated:', data);
        setCampaignData(prev => ({
            ...prev,
            history: data.data
        }));
        
        // Add to realtime updates log
        setRealtimeUpdates(prev => [...prev, {
            type: 'history',
            data: data.data,
            timestamp: new Date()
        }]);
    });

    // Campaign usage created handler
    useSocketEvent('campaign_usage_created', (data) => {
        console.log('New campaign usage created:', data);
        setCampaignData(prev => ({
            ...prev,
            history: [data.data, ...prev.history]
        }));
        
        // Add to realtime updates log
        setRealtimeUpdates(prev => [...prev, {
            type: 'usage_created',
            data: data.data,
            timestamp: new Date()
        }]);
    });

    // Campaign usage updated handler
    useSocketEvent('campaign_usage_updated', (data) => {
        console.log('Campaign usage updated:', data);
        setCampaignData(prev => ({
            ...prev,
            history: prev.history.map(item => 
                item.id === data.data.id ? { ...item, ...data.data } : item
            )
        }));
        
        // Add to realtime updates log
        setRealtimeUpdates(prev => [...prev, {
            type: 'usage_updated',
            data: data.data,
            timestamp: new Date()
        }]);
    });

    // Refund calculation created handler
    useSocketEvent('refund_calculation_created', (data) => {
        console.log('Refund calculation created:', data);
        // Add to realtime updates log
        setRealtimeUpdates(prev => [...prev, {
            type: 'refund_calculation',
            data: data.data,
            timestamp: new Date()
        }]);
    });

    // Request campaign statistics
    const requestCampaignStatistics = useCallback(() => {
        if (connected) {
            emit('request_campaign_statistics');
        }
    }, [connected, emit]);

    // Request campaign history
    const requestCampaignHistory = useCallback(() => {
        if (connected) {
            emit('request_campaign_history');
        }
    }, [connected, emit]);

    // Subscribe to campaign updates
    const subscribeToCampaignUpdates = useCallback(() => {
        if (connected) {
            emit('subscribe', {
                type: 'campaign_updates'
            });
        }
    }, [connected, emit]);

    // Unsubscribe from campaign updates
    const unsubscribeFromCampaignUpdates = useCallback(() => {
        if (connected) {
            emit('unsubscribe', {
                type: 'campaign_updates'
            });
        }
    }, [connected, emit]);

    // Initialize campaign tracking
    useEffect(() => {
        if (connected) {
            console.log('Campaign tracking WebSocket connected');
            setLoading(false);
            
            // Subscribe to updates
            subscribeToCampaignUpdates();
            
            // Request initial data
            requestCampaignStatistics();
            requestCampaignHistory();
        }
    }, [connected, subscribeToCampaignUpdates, requestCampaignStatistics, requestCampaignHistory]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            unsubscribeFromCampaignUpdates();
        };
    }, [unsubscribeFromCampaignUpdates]);

    return {
        // Data
        campaignData,
        realtimeUpdates,
        loading,
        connected,
        error,
        
        // Methods
        requestCampaignStatistics,
        requestCampaignHistory,
        subscribeToCampaignUpdates,
        unsubscribeFromCampaignUpdates
    };
};

export default useCampaignTracking;